import { createSiteAnchor, SiteAnchor, SiteGrid, SiteSettings } from "../models/SiteAnchor";
import { Visual } from "../models/Visual";
import { BaseVisual } from "./BaseVisual";
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import * as THREE from "three";
import { buildWall, ensureChild } from "./visual-utils";
import { UserEventType } from "../panels/worldpanelparts/UserEventer";
import { deleteRecord } from "../api/connection";
import { CollisionBox } from "../models/Model";
import { PhysXObject } from "../panels/worldpanelparts/physx/PhysXObject";
import { IAgent } from "./ModelBasedVisual";
import { LightType, LightVisual } from "./LightVisual";

export class SiteAnchorVisual extends BaseVisual implements SiteAnchor {
  public scenarioAgent?: IAgent;
  public mgrs: string;
  public tileBlobId = '';
  public grid: SiteGrid = {
    size: 100, // Default grid size
    resolution: 10, // Default grid resolution
    blobId: '', // Optional blob ID for grid data
  };
  public active = false;
  public settings: SiteSettings = { lastEdit: Date.now() };
  private siteObj!: THREE.Group;
  private floor!: THREE.Mesh;
  public visuals = new Map<string, BaseVisual>();
  public showBox = true;
  public showGrid = true;
  public representingVisualId = '';
  private axisForSelected = new THREE.AxesHelper(10);
  constructor() {
    super();
    this.type = 'siteAnchor';
    this.mgrs = '';
    this.collection = 'siteanchors';
    this.axisForSelected.name = 'axisForSelected';
    this.axisForSelected.position.z = 0.5; // Position the axes helper slightly above the ground
  }

  public highlightSelectVisual(visual: BaseVisual | null) {
    this.axisForSelected.removeFromParent();
    if (visual && visual.obj) {
      visual.obj.add(this.axisForSelected);
    }
  }

  public getType(): string {
    return 'SiteAnchorVisual';
  }

  public focus(): void {
    this.wr.planetService.pointCameraTo(this.location);
  }

  public addVisual<T extends BaseVisual>(visual: T): void {
    this.visuals.set(visual._id, visual);
    this.obj.add(visual.obj);
  }

  public updateObj(): void {
    if (this.active) {
      this.buildActive();
    } else {
      const label = this.obj.getObjectByName('siteLabel') as THREE.Mesh;
      if (!label) {
        console.warn('SiteAnchorVisual: Label mesh not found');
        return;
      }
      label.geometry.dispose(); // Dispose of the old geometry
      label.geometry = new TextGeometry(this.id || this.mgrs, {
        font: this.wr.dataService.font,
        size: 5,
        depth: 0.5
      });
    }
  }

  protected async buildObj(): Promise<THREE.Object3D> {
    this.siteObj = new THREE.Group();
    if (this.active) {
      this.buildActive();
    } else {
      this.buildInactive();
    }
    return this.siteObj;
  }

  public convertToLocal(point: THREE.Vector3): THREE.Vector3 {
    return this.obj.worldToLocal(point.clone());
  }

  private buildInactive() {
    const site = this.siteObj;
    if (this.representingVisualId) {
      const loader = async () => {
        const repVis = await this.wr.dataService.loadVisual(this.representingVisualId);
        if (repVis) {
          site.add(repVis.obj);
          const light = new LightVisual();
          light.lightType = LightType.Directional;
          light.intensity = 3.5;
          light.color = 0xffffff;
          light.position = { x: 0, y: 0, z: 150 };
          await light.load(this.wr);
          site.add(light.obj);
          light.obj.name = 'representingVisualLight';
        }
        const towerRadius = 20;
        const label = new THREE.Mesh(
          new TextGeometry(this.id || this.mgrs, {
            font: this.wr.dataService.font,
            size: 500,
            depth: 5
          }),
          new THREE.MeshBasicMaterial({ color: 0xffffff })
        );
        label.name = 'siteLabel';
        label.rotation.y = -Math.PI / 2;
        label.position.set(0, towerRadius * 1.25, 100);
        site.add(label);
        const bankHeight = 120000;
        const geometry = new THREE.CylinderGeometry(towerRadius, towerRadius, bankHeight, 32);
        const material = new THREE.MeshStandardMaterial({
          color: 0xff0000,
          emissive: 0xff0000,
          emissiveIntensity: 14.5
        });
        const cylinder = new THREE.Mesh(geometry, material);
        cylinder.rotation.x = Math.PI / 2; // Rotate to point up the z-axis
        cylinder.position.set(0, 0, bankHeight / 2); // Position it above
        // site.add(cylinder);
      };
      loader().catch(console.error);
    } else {
      const geometry = new THREE.CylinderGeometry(10, 10, 5, 32);
      const material = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
      const cylinder = new THREE.Mesh(geometry, material);
      cylinder.rotation.x = Math.PI / 2; // Rotate to point up the z-axis
      cylinder.position.set(0, 0, 0); // Position it above
      const axis = new THREE.AxesHelper(20);
      axis.position.set(0, 0, 5); // Position the axes helper above the cylinder
      site.add(axis);
      site.add(cylinder);
      const label = new THREE.Mesh(
        new TextGeometry(this.id || this.mgrs, {
          font: this.wr.dataService.font,
          size: 5,
          depth: 0.5
        }),
        new THREE.MeshBasicMaterial({ color: 0xffffff })
      );
      label.name = 'siteLabel';
      label.rotation.z = -Math.PI / 2;
      label.position.set(0, 0, 6);
      site.add(label);
    }
  }

  private buildActive() {
    // this.siteObj.clear();
    const scaffoldingMaterial =
      new THREE.MeshBasicMaterial({
        color: 0x00ff00,
        transparent: true, opacity: 0.15
      });
    const hgs = this.grid.size / 2;
    const border = ensureChild(this.siteObj, 'border', THREE.Group);
    border.clear();
    buildWall(border, [
      new THREE.Vector3(-hgs, -hgs, 0),
      new THREE.Vector3(hgs, -hgs, 0),
      new THREE.Vector3(hgs, hgs, 0),
      new THREE.Vector3(-hgs, hgs, 0)
    ], 5, 0.5, scaffoldingMaterial);
    border.visible = this.showBox;
    this.wr.planetService.tileRenderingOnOff(!this.showBox); // Hide the tiler group if the box is shown
    this.siteObj.getObjectByName('SiteGrid')?.removeFromParent();
    const gridColor = 0xa8a8a8;
    const gridHelper = new THREE.GridHelper(this.grid.size, this.grid.size, gridColor, gridColor);
    gridHelper.rotation.x = Math.PI / 2; // Rotate to align with the ground
    gridHelper.position.set(0, 0, 0.005); // Position slightly above the ground to avoid z-fighting
    gridHelper.name = 'SiteGrid';
    this.siteObj.add(gridHelper);
    // const axis = new THREE.AxesHelper(20);
    // axis.position.set(0, 0, 10); // Position the axes helper above
    // this.siteObj.add(axis);
    this.floor = ensureChild(this.siteObj, 'floor', THREE.Mesh);
    this.floor.geometry.dispose();
    this.floor.geometry = new THREE.PlaneGeometry(this.grid.size, this.grid.size);
    this.floor.material = new THREE.MeshBasicMaterial({
      // color: 0xb0b0b0,
      color: 0x208020,
      side: THREE.DoubleSide,
      // transparent: true,
      // opacity: 0.5
    });
    this.floor.visible = this.showBox;
    gridHelper.visible = this.showGrid;
    this.siteObj.add(this.floor);
  }

  public getEventerTargets(eventType: UserEventType): THREE.Object3D[] {
    if (eventType === UserEventType.Click) {
      const visuals = Array.from(this.visuals.values()).map(v => v.obj);
      return this.floor ? [...visuals, this.floor] : [...visuals];
    } else if (eventType === UserEventType.MouseMove) {
      return this.floor ? [this.floor] : [];
    } else {
      console.warn(`Unknown UserEventType: ${eventType}`);
      return [];
    }
  }

  render(delta: number): void {
    if (this.scenarioAgent) {
      this.scenarioAgent.render(delta);
    }
  }

  public createDataTemplate(): Visual {
    const data = createSiteAnchor(this._id);
    return data;
  }

  private clearSite() {
    if (this.siteObj) {
      this.siteObj.clear();
    }
  }

  public async save(): Promise<void> {
    if (this.active) {
      this.settings.cameraPosition = this.wr.getCameraPosition();
      this.settings.lastEdit = Date.now();
    }
    await super.save();
  }

  async removeVisual(visual: BaseVisual) {
    this.visuals.delete(visual._id);
    this.wr.dataService.removeVisual(visual);
    await deleteRecord('visuals', visual);
  }


  public activate() {
    this.clearSite();
    this.active = true;
    this.buildActive();
  }

  public deactivate() {
    this.clearSite();
    this.active = false;
    this.buildInactive();
  }

  public getPhysXObject(): PhysXObject {
    const physXObj = super.getPhysXObject();
    physXObj.position = { x: 0, y: 0, z: 0 };
    physXObj.rotation = { x: 0, y: 0, z: 0 };
    return physXObj;
  }

  protected getCollisionBoxes(): CollisionBox[] {
    return [
      {
        size: { x: this.grid.size, y: this.grid.size, z: 5 },
        position: { x: 0, y: 0, z: -2.5 },
        rotation: { x: 0, y: 0, z: 0 },
      }
    ];
  }

}
